import { connectionManager } from "./session-connection-manager.service";
import { debugLog, debugWarn, debugError } from "../common/utils/debug";
import { audioService } from "./audio.service";
import { azureCustomAvatarService } from "./azure-custom-avatar.service";
import { signalRService } from "./signalr.service";
import { speechToTextSileroService } from './speech-to-text-silero.service';
import { base64ToArrayBuffer } from "../common/utils/audio.utils";
import {
  createUserMessage,
  createAssistantMessage,
  createSystemMessage,
} from "../common/utils/message.utils";
import { ToolCall } from "../components/shared/ToolsPanel";
import { toast } from "../components/shared/ToastNotification";
import { createStateUpdater } from "../common/utils/state";
import { SOCKET_EVENTS } from "../common/constants/app.constants";
import { getMessageStartTime, setMessageStartTime } from "../hooks/useMessageHandling";

// Flag to track when manual send is in progress - prevents duplicate messages from audio events
let manualSendInProgress = false;

export const setManualSendInProgress = (value: boolean) => {
  debugLog(`🚦 [Flag] setManualSendInProgress(${value}) at:`, Date.now());
  debugLog(`🚦 [Flag] Previous value was:`, manualSendInProgress);
  manualSendInProgress = value;
};

export const isManualSendInProgress = () => {
  debugLog(`🚦 [Flag] isManualSendInProgress() called, returning:`, manualSendInProgress);
  return manualSendInProgress;
};

// Store the current avatar speech handler so we can remove it before adding a new one
// This prevents multiple handlers from firing when TURN_END event occurs
let currentSpeechEndedHandler: (() => void) | null = null;

// NEW: Queue metadata for each sentence (FIFO - matches speech order)
// Each sentence received pushes metadata to queue
// Each TURN_START pops metadata from queue
let pendingMetadataQueue: Array<{
  isLastSentence: boolean;
  sentenceIndex: number;
  totalSentences: number;
  isProcessExplanation: boolean;
  isConfirmationPrompt: boolean;
  isTroubleshooting: boolean;
  isGreeting: boolean;
  isTroubleshootingFailure: boolean;
  isNoResponseTimeout: boolean;
  isClosingMessage: boolean;
}> = [];

export interface ConnectionEventHandlers {
  addMessage: (message: any) => void;
  updateLastMessage: (text: string) => void;
  setSessionState: (state: any) => void;
  setAudioState: (state: any) => void;
  addToolCall: (toolCall: ToolCall) => void;
  updateToolCall: (id: string, update: Partial<ToolCall>) => void;
  getSessionId?: () => string | null; // Add optional getter for session ID
  setMuted?: (muted: boolean) => void; // Add optional setter for mute state
  autoUnmute?: () => void; // Auto-unmute that respects manual mute preference
  setIsThinking?: (thinking: boolean) => void; // Set thinking indicator state
}

/**
 * !Registers client listeners for server-emitted session/connection events
 * @param handlers - Callback functions for state management
 * @returns Cleanup function to remove all event listeners
 */
export function setupConnectionEventHandlers(
  handlers: ConnectionEventHandlers
): () => void {
  const {
    addMessage,
    updateLastMessage,
    setSessionState,
    setAudioState,
    addToolCall,
    updateToolCall,
    getSessionId,
    setMuted,
    autoUnmute,
    setIsThinking,
  } = handlers;
  const updateSessionState = createStateUpdater(setSessionState);
  const updateAudioState = createStateUpdater(setAudioState);
  let currentSessionId: string | null = null;
  let isSpeaking: boolean = false; // Flag to prevent overlapping speech
  // Queue for speech before avatar is ready - includes full data for chat message display
  let pendingSpeech: Array<{
    message: string;
    emotion: string;
    fullMessage: string;
    messageId: string;
    sentenceIndex: number;
    totalSentences: number;
    ssml?: string;
    messageType?: string;
    isNoResponsePrompt?: boolean;
    isClosing?: boolean;
  }> = [];
  let isAvatarReady = false;
  let storedAvatarConfig: { avatarSessionId: string; avatarConfig: any } | null = null;
  let processedMessageIds = new Set<string>(); // Track processed messages to prevent duplicates
  let avatarReadyEventFired = false; // Prevent duplicate avatar_ready handling
  let greetingMessagesCount = 0; // Track greeting + processExplanation messages
  let currentAvatarIdleHandler: (() => void | Promise<void>) | null = null; // Track current idle handler to prevent duplicates
  // Queue for pending chat messages - each first-sentence pushes here, TURN_START pops (fixes race condition with multiple messages)
  let pendingChatMessageQueue: Array<{ fullMessage: string; messageId: string }> = [];
  let isProcessingClosingMessage = false; // Track if we're currently processing a closing message sequence

  // Listen for avatar ready event from Avatar component
  const handleAvatarReadyFromComponent = () => {
    if (avatarReadyEventFired) {
      debugLog("⏭️ Avatar ready already processed, skipping duplicate");
      return;
    }
    
    avatarReadyEventFired = true;
    debugLog("🎭 Avatar component is ready, updating state");
    isAvatarReady = true;
    updateSessionState({ avatarReady: true });
    
    // Play any pending speech that arrived before avatar was ready
    if (pendingSpeech.length > 0) {
      debugLog(`🔊 Playing ${pendingSpeech.length} pending speech request(s)`);
      pendingSpeech.forEach((speechData) => {
        const { message, emotion, fullMessage, messageId, sentenceIndex, totalSentences, ssml, messageType, isClosing } = speechData;
        debugLog("🔊 Playing pending message:", message.substring(0, 50) + "...");
        
        // CRITICAL: Push to chat message queue for first sentence so it appears in chat
        if (sentenceIndex === 0) {
          debugLog(`💬 [PendingSpeech] Pushing chat message to queue (queue length: ${pendingChatMessageQueue.length + 1}):`, fullMessage.substring(0, 50) + "...");
          pendingChatMessageQueue.push({ fullMessage, messageId });
        }
        
        // CRITICAL: Push metadata to queue so TURN_START handler can process it
        const isGreeting = greetingMessagesCount === 1;
        const isProcessExplanation = greetingMessagesCount === 2;
        const isTroubleshooting = messageType === "troubleshooting";
        const isTroubleshootingFailure = messageType === "troubleshootingFailure";
        const isNoResponseTimeout = messageType === "noResponseTimeout";
        const isConfirmationPrompt = messageType === "confirmationPrompt";
        const isClosingMessage = isClosing === true;
        const isLastSentence = sentenceIndex === totalSentences - 1;
        
        debugLog(`📋 [PendingSpeech] Pushing metadata to queue: sentence ${sentenceIndex + 1}/${totalSentences}, isLastSentence=${isLastSentence}`);
        pendingMetadataQueue.push({
          isLastSentence,
          sentenceIndex,
          totalSentences,
          isProcessExplanation,
          isConfirmationPrompt,
          isTroubleshooting,
          isGreeting,
          isTroubleshootingFailure,
          isNoResponseTimeout,
          isClosingMessage
        });
        
        // Use SSML if available (optimized path)
        if (ssml) {                    
          azureCustomAvatarService.speakSSML(ssml)
            .then(() => {
              debugLog("✅ Pending speech (SSML) played successfully");
              updateAudioState({ isSpeaking: false });
            })
            .catch((error) => {
              debugError("❌ Failed to synthesize pending avatar speech (SSML):", error);
              updateAudioState({ isSpeaking: false });
            });
        } else {
          azureCustomAvatarService.speak(message, emotion)
            .then(() => {
              debugLog("✅ Pending speech played successfully");
              updateAudioState({ isSpeaking: false });
            })
            .catch((error) => {
              debugError("❌ Failed to synthesize pending avatar speech:", error);
              updateAudioState({ isSpeaking: false });
            });
        }
      });
      pendingSpeech = [];
    } else {
      debugLog("📝 No pending speech to play");
    }
  };

  // Listen for avatar requesting config (if component mounted after session_started)
  const handleAvatarRequestConfig = () => {
    debugLog("Avatar component requesting config, stored config:", storedAvatarConfig);
    if (storedAvatarConfig) {
      window.dispatchEvent(new CustomEvent('avatar_config_ready', {
        detail: storedAvatarConfig
      }));
    }
  };

  window.addEventListener('avatar_ready', handleAvatarReadyFromComponent);
  window.addEventListener('avatar_request_config', handleAvatarRequestConfig);

  // Listen for avatar starting to speak (TURN_START), then add message to chat IMMEDIATELY
  // Since we now properly wait for ICE connected before avatar_ready, no delay is needed
  
  const handleAvatarSpeakingStart = () => {
    // Pop from queue (FIFO) - each first-sentence pushed here, we pop when TURN_START fires
    if (pendingChatMessageQueue.length > 0) {
      const messageToAdd = pendingChatMessageQueue.shift()!;
      debugLog(`💬 [SessionEvents] Popped chat message from queue (${pendingChatMessageQueue.length} remaining):`, messageToAdd.fullMessage.substring(0, 50) + "...");
      
      // Add message to chat IMMEDIATELY - no delay needed since avatar_ready waits for ICE connected
      debugLog("🔊 [SessionEvents] TURN_START - adding message to chat NOW:", messageToAdd.fullMessage.substring(0, 50) + "...");
      const assistantMessage = createAssistantMessage(messageToAdd.fullMessage);
      addMessage(assistantMessage);
    }

    // Pop metadata for THIS sentence that just started speaking (FIFO queue)
    const currentMetadata = pendingMetadataQueue.shift();
    
    if (!currentMetadata) {
      debugLog('⚠️ [SessionEvents] TURN_START fired but no metadata in queue - ignoring');
      return;
    }
    
    debugLog(`🎬 [SessionEvents] Sentence ${currentMetadata.sentenceIndex + 1}/${currentMetadata.totalSentences} started speaking (isLastSentence: ${currentMetadata.isLastSentence})`);

    // CRITICAL: Only register TURN_END handler when LAST sentence STARTS speaking
    // This prevents early sentences from triggering the handler
    if (currentMetadata.isLastSentence) {
      debugLog(`✅ [SessionEvents] LAST sentence started speaking - registering TURN_END handler NOW`);
      
      // Remove any previous handler
      if (currentSpeechEndedHandler) {
        debugLog(`🧹 [SessionEvents] Removing stale TURN_END handler from previous message`);
        window.removeEventListener('avatar-speech-ended', currentSpeechEndedHandler);
        currentSpeechEndedHandler = null;
      }

      const handleSpeechEnded = () => {
        debugLog(`⚡ [SessionEvents] TURN_END fired for LAST sentence - unmuting NOW!`);
        
        debugLog('🔊 [SessionEvents] Setting isSpeaking to FALSE immediately on TURN_END');
        updateAudioState({ isSpeaking: false });
        
        // Unmute logic based on message type (use currentMetadata from closure)
        if (currentMetadata.isProcessExplanation) {
          debugLog('🎤 [SessionEvents] INSTANT unmute after processExplanation (all sentences complete)');
          speechToTextSileroService.setMuted(false);
          if (setMuted) setMuted(false);
        } else if (currentMetadata.isConfirmationPrompt) {
          // Don't unmute - next question starts immediately
        } else if (currentMetadata.isTroubleshooting) {
          debugLog('🎤 [SessionEvents] INSTANT unmute after troubleshooting (all sentences complete)');
          speechToTextSileroService.setMuted(false);
          if (setMuted) setMuted(false);
        } else if (!currentMetadata.isGreeting && !currentMetadata.isTroubleshootingFailure && !currentMetadata.isNoResponseTimeout && !currentMetadata.isClosingMessage) {
          debugLog('🎤 [SessionEvents] INSTANT unmute after avatar speech (all sentences complete)');
          speechToTextSileroService.setMuted(false);
          if (autoUnmute) autoUnmute();
        }
        
        window.removeEventListener('avatar-speech-ended', handleSpeechEnded);
        currentSpeechEndedHandler = null;
      };

      currentSpeechEndedHandler = handleSpeechEnded;
      window.addEventListener('avatar-speech-ended', handleSpeechEnded, { once: false });
    }
  };
  
  window.addEventListener('avatar-speaking-start', handleAvatarSpeakingStart);

  const handleSessionStarted = async (data: any) => {
    const avatarInitStart = performance.now();
    
    updateSessionState({
      isConnected: true,
      isConnecting: false,
      sessionId: data.sessionId,
      isUsingFallback: data.isUsingFallback,
      avatarReady: false, // Will be set to true after avatar initializes
    });

    if (data.sessionId) {
      connectionManager.setSessionId(data.sessionId);
      currentSessionId = data.sessionId;
    }

    // Store avatar config for later initialization (after UI is visible)
    if (data.avatarSessionId && data.avatarConfig) {
      debugLog("Storing avatar config for initialization after UI is ready:", {
        sessionId: data.avatarSessionId,
        region: data.avatarConfig.serviceRegion,
        character: data.avatarConfig.avatarCharacter,
        voice: data.avatarConfig.voiceName,
        customized: data.avatarConfig.customized,
        endpoint: data.avatarConfig.endpoint
      });
      
      // Store config for late-mounting Avatar component
      storedAvatarConfig = {
        avatarSessionId: data.avatarSessionId,
        avatarConfig: {
          subscriptionKey: data.avatarConfig.subscriptionKey,
          serviceRegion: data.avatarConfig.serviceRegion,
          voiceName: data.avatarConfig.voiceName,
          avatarCharacter: data.avatarConfig.avatarCharacter,
          avatarStyle: data.avatarConfig.avatarStyle,
          customized: data.avatarConfig.customized,
          useBuiltInVoice: data.avatarConfig.useBuiltInVoice,
          voiceSpeed: data.avatarConfig.voiceSpeed || 1.0,
          deploymentId: data.avatarConfig.deploymentId,
          endpoint: data.avatarConfig.endpoint
        }
      };
      
      // Dispatch event in case Avatar component is already mounted
      window.dispatchEvent(new CustomEvent('avatar_config_ready', {
        detail: storedAvatarConfig
      }));
    } else {
      debugWarn("No avatar config provided, proceeding without video avatar");
      updateSessionState({ avatarReady: true });
    }
  };

  const handleAvatarSpeaking = (data: any) => {
    // Avatar speaking event from backend - synthesize speech through Azure avatar
    debugLog("📨 Received avatar_speaking event:", {
      hasMessage: !!data?.message,
      messagePreview: data?.message?.substring(0, 50),
      emotion: data?.emotion,
      isAvatarReady,
      messageId: data?.messageId,
      sentenceIndex: data?.sentenceIndex,
      totalSentences: data?.totalSentences,
      messageType: data?.messageType,
      hasFullMessage: !!data?.fullMessage
    });
    
    if (data?.message && azureCustomAvatarService) {
      // Use messageId from backend for progressive synthesis grouping
      const messageId = data.messageId || `${Date.now()}_${data.message.substring(0, 20)}`;
      const sentenceIndex = data.sentenceIndex ?? 0;
      const totalSentences = data.totalSentences ?? 1;
      const fullMessage = data.fullMessage || data.message;
      
      // Check for duplicate messages
      if (processedMessageIds.has(messageId + '_' + sentenceIndex)) {
        debugWarn("⚠️ Duplicate avatar_speaking event detected, ignoring:", messageId, sentenceIndex);
        return;
      }
      
      processedMessageIds.add(messageId + '_' + sentenceIndex);
      // Clean up old IDs (keep only last 20)
      if (processedMessageIds.size > 20) {
        const idsArray = Array.from(processedMessageIds);
        processedMessageIds = new Set(idsArray.slice(-20));
      }
      
      const emotion = data.emotion || "professional";
      
      // Only add to chat on FIRST sentence of a message group
      if (sentenceIndex === 0) {
        // Hide thinking indicator - response has arrived
        if (setIsThinking) {
          debugLog("💭 [SessionEvents] Setting isThinking to FALSE (response arrived)");
          setIsThinking(false);
        }

        debugLog("💬 Preparing message for chat (will add when avatar starts speaking):", fullMessage.substring(0, 50) + "...");
        
        // Push to chat message queue - will be popped when TURN_START fires for this sentence
        debugLog(`📋 [SessionEvents] Pushing chat message to queue (queue length: ${pendingChatMessageQueue.length + 1})`);
        pendingChatMessageQueue.push({ fullMessage, messageId });

        // Track greeting flow messages (only count unique message groups)
        greetingMessagesCount++;
      } else {
        debugLog(`📝 Sentence ${sentenceIndex + 1}/${totalSentences} - skipping chat add (already displayed)`);
      }
      
      const isGreeting = greetingMessagesCount === 1;
      const isProcessExplanation = greetingMessagesCount === 2;
      const isTroubleshooting = data.messageType === "troubleshooting"; // From backend
      const isTroubleshootingFailure = data.messageType === "troubleshootingFailure"; // Final failure - will terminate
      const isNoResponseTimeout = data.messageType === "noResponseTimeout"; // No response timeout - will terminate
      const isConfirmationPrompt = data.messageType === "confirmationPrompt"; // Backend explicitly marks confirmation prompt
      const isTransitionConfirmation = data.messageType === "transitionConfirmation"; // "Ready for next question?" prompt
      const isNoResponsePrompt = data.isNoResponsePrompt === true; // Backend marks rephrased timeout prompts
      const isClosingMessage = data.isClosing === true;
      
      // Track if we're in a closing message sequence (for multi-sentence closing messages)
      if (sentenceIndex === 0 && isClosingMessage) {
        isProcessingClosingMessage = true;
        debugLog("🏁 [SessionEvents] Starting closing message sequence");
      }
      
      // Detect if this is an interview question (not greeting, process, confirmation, etc.)
      const isInterviewQuestion = !isGreeting && !isProcessExplanation && !isConfirmationPrompt && 
                                  !isTroubleshooting && !isTroubleshootingFailure && 
                                  !isNoResponseTimeout && !isNoResponsePrompt && !isClosingMessage &&
                                  !isTransitionConfirmation;
      
      debugLog(`🔍 [SessionEvents] Message classification: messageType=${data.messageType ?? '(null)'}, isConfirmationPrompt=${isConfirmationPrompt}, isNoResponsePrompt=${isNoResponsePrompt}, isNoResponseTimeout=${isNoResponseTimeout}, isInterviewQuestion=${isInterviewQuestion}, greetingMessagesCount=${greetingMessagesCount}`);
      
      // Update audio state FIRST so isSpeaking=true prevents useEffect from restarting mic
      debugLog('🔊 [SessionEvents] Setting isSpeaking to TRUE before avatar speaks');
      updateAudioState({ isSpeaking: true });
      
      // DON'T mute or block microphone - we need to detect user interruptions
      // Just keep the microphone running to monitor for user speech
      debugLog('👂 [SessionEvents] Keeping microphone active to detect interruptions');
      
      // After first message, avatar should always be ready
      // Only queue if this is literally the first speech before avatar initialized
      const shouldSpeak = isAvatarReady || greetingMessagesCount > 1;
      debugLog("🔍 [SessionEvents] Speech decision:", { isAvatarReady, greetingMessagesCount, shouldSpeak });
      
      if (shouldSpeak) {
        // Avatar is ready, speak immediately
        debugLog("🔊 Avatar is ready, speaking immediately...");
        debugLog("🔊 [SessionEvents] Azure avatar service state:", {
          isReady: (azureCustomAvatarService as any).isReady,
          hasSynthesizer: !!(azureCustomAvatarService as any).avatarSynthesizer,
          currentlySpeaking: (azureCustomAvatarService as any).currentlySpeaking,
          queueLength: (azureCustomAvatarService as any).speechQueue?.length ?? 0
        });

        // Set up one-time listener for when avatar finishes and goes idle
        // ONLY process on the LAST sentence of a message group to avoid duplicate notifications
        const isLastSentence = sentenceIndex === totalSentences - 1;
        
        debugLog(`📊 [SessionEvents] Sentence ${sentenceIndex + 1}/${totalSentences} - isLastSentence: ${isLastSentence}`);
        
        // Push metadata to queue - will be popped when TURN_START fires for this sentence
        pendingMetadataQueue.push({
          isLastSentence,
          sentenceIndex,
          totalSentences,
          isProcessExplanation,
          isConfirmationPrompt,
          isTroubleshooting,
          isGreeting,
          isTroubleshootingFailure,
          isNoResponseTimeout,
          isClosingMessage
        });
        
        if (isLastSentence) {
          debugLog(`🎯 [SessionEvents] Queued LAST sentence metadata - TURN_END handler will register when this sentence starts speaking`);
        } else {
          debugLog(`⏭️ [SessionEvents] Queued non-last sentence metadata - no handler will be registered`);
        }
        
        debugLog(`📋 [SessionEvents] Metadata queue length: ${pendingMetadataQueue.length}`);
        
        const handleAvatarIdle = async () => {
          debugLog("✅ [SessionEvents] Avatar went idle - speech truly completed");
          
          // Only execute logic for the last sentence in the group
          if (!isLastSentence) {
            debugLog(`⏭️ [SessionEvents] Skipping idle handler - not last sentence (${sentenceIndex + 1}/${totalSentences})`);
            return;
          }
          
          debugLog(`🎯 [SessionEvents] Processing idle for LAST sentence (${sentenceIndex + 1}/${totalSentences})`);
          // isSpeaking already set to false in handleSpeechEnded (TURN_END event) for instant UI response

          // Clear microphone block - let useAudioMute effect handle actual recording restart
          // This respects mute state: if user manually muted, mic won't restart
          debugLog('🔓 [SessionEvents] Clearing microphone block (useAudioMute will handle recording)');
          speechToTextSileroService.clearBlock();
            
          // If greeting just finished, tell backend to send processExplanation
          if (isGreeting) {
            debugLog("📞 [SessionEvents] Greeting finished, notifying backend to send processExplanation...");
            await signalRService.notifyGreetingCompleted();
          }
          
          // After processExplanation (which asks "can you hear me?"), UNMUTE so user can respond
          // DO NOT call OnConfirmationCompleted here - wait for user's "yes" response
          if (isProcessExplanation) {
            debugLog("✅ [SessionEvents] ProcessExplanation finished - backend notification");
            debugLog("⏸️ [SessionEvents] Microphone already unmuted via TURN_END event");
            debugLog("📋 [SessionEvents] Backend will automatically send confirmation prompt after audio is confirmed");
            // Notify backend that processExplanation speech completed - this starts the no-response timer
            debugLog("⏱️ [SessionEvents] Notifying backend that processExplanation completed - will start no-response timer");
            await signalRService.notifyProcessExplanationCompleted();
            // User's "yes" response will be processed by backend via ProcessTextInput
            // Backend will validate audio confirmation and automatically send confirmation prompt
            // NO need to call notifyConfirmationCompleted here
          }
          
          // After no-response prompt (rephrased question), notify backend so timer can reschedule
          if (isNoResponsePrompt) {
            debugLog("⏱️ [SessionEvents] No-response prompt finished - notifying backend to reschedule timer");
            await signalRService.notifyNoResponsePromptCompleted();
          }
          
          // After interview question speech completes, notify backend to start no-response timer
          if (isInterviewQuestion) {
            debugLog("⏱️ [SessionEvents] Interview question finished - notifying backend to start no-response timer");
            await signalRService.notifyQuestionSpeechCompleted();
            
            // Recognizer is already running (kept warm) - just unmute to resume audio capture
            // No need to restart streaming - this eliminates 100-200ms Azure SDK reconnection delay
            debugLog("✅ [SessionEvents] Recognizer already warm - unmute will resume audio capture");
          }
          
          // After troubleshooting message, UNMUTE and WAIT for user response
          // Notify backend that troubleshooting message completed so timer can reschedule
          if (isTroubleshooting) {
            debugLog("🔧 [SessionEvents] Troubleshooting message finished - notifying backend and unmuting for user response");
            debugLog("⏱️ [SessionEvents] Backend will schedule timeout timer for next troubleshooting attempt");
            debugLog("⏸️ [SessionEvents] User should respond 'yes' (if fixed) or 'no' (still has issues)");
            debugLog("📋 [SessionEvents] Backend will handle response and decide next action");
            
            // Notify backend first so timer starts
            await signalRService.notifyTroubleshootingMessageCompleted();
            
            debugLog('📋 [SessionEvents] Microphone already unmuted via TURN_END event');
            // User's response will be processed by backend via ProcessTextInput
            // Backend will either proceed to confirmation or send next troubleshooting
            return; // Important: Don't proceed to confirmation logic below
          }
          
          // After troubleshooting FAILURE, the interview is terminated
          // DO NOT unmute - session will end via closing message handler below
          if (isTroubleshootingFailure) {
            debugLog("❌ [SessionEvents] Troubleshooting failure - max attempts exceeded");
            debugLog("🏁 [SessionEvents] Interview will terminate automatically");
            debugLog("📋 [SessionEvents] Backend marked interview as complete (isClosing should be true)");
            // Do NOT return here - let the isClosingMessage handler run below
            // Just skip unmuting since the interview is ending
          }
          
          // After no-response timeout, the interview is terminated
          // DO NOT unmute - session will end via closing message handler below
          if (isNoResponseTimeout) {
            debugLog("⏱️ [SessionEvents] No response timeout - max attempts exceeded");
            debugLog("🏁 [SessionEvents] Interview will terminate automatically");
            debugLog("📋 [SessionEvents] Backend marked interview as complete (isClosing should be true)");
            // Do NOT return here - let the isClosingMessage handler run below
            // Just skip unmuting since the interview is ending
          }
          
          // If confirmation prompt finished, tell backend to send first question
          // NOTE: Don't auto-unmute here - the first question starts immediately after
          // and we should wait until THAT finishes before unmuting the user
          if (isConfirmationPrompt) {
            debugLog("📞 [SessionEvents] Confirmation prompt finished, notifying backend to send first question...");
            await signalRService.notifyConfirmationCompleted();
            // User will be auto-unmuted after the first interview question finishes speaking
          } else if (!isProcessExplanation && !isGreeting && !isTroubleshootingFailure && !isNoResponseTimeout) {
            // For all other messages (questions, etc.), microphone already unmuted via TURN_END event
            // Note: processExplanation already unmuted above, keep it unmuted
            // Note: troubleshooting failure and no-response timeout don't unmute - interview is ending
            // This respects manual mute - if user muted themselves, they stay muted
            debugLog('📋 [SessionEvents] Microphone already unmuted via TURN_END event (respects manual mute)');
          }
          
          // If closing message finished, notify backend (which will send interview_completed immediately)
          // Only notify on the LAST sentence of a closing message to ensure all speech completes
          if (isClosingMessage && isLastSentence) {
            debugLog("🏁 [SessionEvents] Closing message completed (last sentence), notifying backend...");
            isProcessingClosingMessage = false; // Reset flag
            await signalRService.notifyClosingMessageCompleted();
          }
          
          // Remove this one-time event listener
          window.removeEventListener('avatar-idle', handleAvatarIdle);
          currentAvatarIdleHandler = null; // Clear reference
        };
        
        // Remove any existing avatar-idle handler first to prevent duplicates
        if (currentAvatarIdleHandler) {
          debugLog('🧹 [SessionEvents] Removing previous avatar-idle handler');
          window.removeEventListener('avatar-idle', currentAvatarIdleHandler);
          currentAvatarIdleHandler = null;
        }
        
        // Register the NEW avatar-idle event listener BEFORE speaking
        currentAvatarIdleHandler = handleAvatarIdle;
        window.addEventListener('avatar-idle', handleAvatarIdle, { once: true });
        
        // Shared error handler for speech synthesis failures
        const handleSpeechSynthesisError = (error: unknown) => {
          debugError("❌ Failed to synthesize avatar speech:", error);
          updateAudioState({ isSpeaking: false });

          // Clear microphone block - let useAudioMute effect handle actual recording restart
          debugLog('🔓 [SessionEvents] Clearing microphone block after error (useAudioMute will handle recording)');
          speechToTextSileroService.clearBlock();

          // Remove event listener on error
          window.removeEventListener('avatar-idle', handleAvatarIdle);

          // Auto-unmute user on error to prevent being stuck muted (respects manual mute)
          if (autoUnmute && !isProcessExplanation) {
            debugLog('🎤 [SessionEvents] Auto-unmuting user after error (respects manual mute)');
            autoUnmute();
          }
        };

        // Start avatar speaking (will trigger avatar-idle event when done)
        // Use backend-generated SSML if available (optimization: saves 3-5ms per sentence)
        if (data.ssml) {
          debugLog('📝 [SessionEvents] Using backend-generated SSML (optimized)');
          
          // Wrap Arabic greeting and goodbye with Arabic audio tag for proper pronunciation
          let ssmlToSpeak = data.ssml;
          const arabicGreetingUrl = process.env.REACT_APP_ARABIC_GREETING_AUDIO_URL || 'https://aiavatarstore.blob.core.windows.net/arabic-audio-snippets/assalamu-aleikum.wav';
          const arabicGoodbyeUrl = process.env.REACT_APP_ARABIC_GOODBYE_AUDIO_URL || 'https://aiavatarstore.blob.core.windows.net/arabic-audio-snippets/shukran.wav';
          
          if (ssmlToSpeak.includes('Assalamu alaikum and hello.')) {
            debugLog('🌍 [SessionEvents] Wrapping Arabic greeting with audio tag');
            ssmlToSpeak = ssmlToSpeak.replace(
              'Assalamu alaikum and hello.',
              `<audio src="${arabicGreetingUrl}">Assalamu alaikum and hello.</audio>`
            );
          }

          if (ssmlToSpeak.includes('Shukran and thank you.')) {
            debugLog('🌍 [SessionEvents] Wrapping Arabic goodbye with audio tag');
            ssmlToSpeak = ssmlToSpeak.replace(
              'Shukran and thank you.',
              `<audio src="${arabicGoodbyeUrl}">Shukran and thank you.</audio>`
            );
          }

          // Phonetic fix: pronounce "My name is ALIA" correctly
          ssmlToSpeak = ssmlToSpeak.replace('My name is ALIA', 'My name is Ah-LEE-ah');

          azureCustomAvatarService.speakSSML(ssmlToSpeak)
            .then(() => {
              debugLog('✅ [SessionEvents] speakSSML() promise resolved successfully');
            })
            .catch(handleSpeechSynthesisError);
        } else {
          // Fallback: Generate SSML on frontend (backward compatibility)
          debugLog('📝 [SessionEvents] No SSML from backend, generating on frontend (legacy mode)');
          azureCustomAvatarService.speak(data.message, emotion)
            .then(() => {
              debugLog('✅ [SessionEvents] speak() promise resolved successfully');
            })
            .catch(handleSpeechSynthesisError);
        }
      } else {
        // Avatar not ready yet, queue the speech with full data for proper chat display
        debugLog("⏳ Avatar not ready yet, queuing speech with full data (queue size: " + (pendingSpeech.length + 1) + ")");
        pendingSpeech.push({
          message: data.message,
          emotion,
          fullMessage,
          messageId,
          sentenceIndex,
          totalSentences,
          ssml: data.ssml,
          messageType: data.messageType,
          isNoResponsePrompt: data.isNoResponsePrompt,
          isClosing: data.isClosing
        });
        updateAudioState({ isSpeaking: false });
      }
    } else {
      debugWarn("⚠️ avatar_speaking event missing message or service");
    }
  };

  const handleResponseText = (data: any) => {
    if (data.type === "thinking") {
      const thinkingMessage = createAssistantMessage(data.text);
      addMessage(thinkingMessage);
    } else if (data.type === "partial") {
      updateLastMessage(data.text);
    } else if (data.type === "final") {
      const startTime = getMessageStartTime();
      if (startTime) {
        const responseTime = performance.now() - startTime;
      }
      
      let finalText = data.text;
      let downloadLink = data.downloadLink;
      let fileName = data.fileName;

      // Check if backend sent [[Download Summary]] marker
      if (finalText && finalText.includes("[[Download Summary]]")) {
        const sessionId = getSessionId ? getSessionId() : currentSessionId;

        if (sessionId) {
          // Get backend base URL (without /api)
          const backendBaseUrl =
            process.env.REACT_APP_BACKEND_URL ||
            process.env.REACT_APP_SERVER_URL ||
            "http://localhost:5080";

          // Ensure base URL doesn't end with /api
          const cleanBaseUrl = backendBaseUrl.replace(/\/api\/?$/, "");

          // Construct full download URL with /api prefix
          downloadLink = `${cleanBaseUrl}/api/sessions/${sessionId}/summary`;
          fileName = `Summary_${new Date().toISOString().split("T")[0]}.docx`;

          // Replace marker with friendly message
          finalText =
            "I've prepared a summary for you. You can download it from the link in the chat.";
        }
      }

      const assistantMessage = createAssistantMessage(
        finalText,
        downloadLink,
        fileName
      );
      addMessage(assistantMessage);

      // START SPEECH GENERATION IMMEDIATELY - Don't wait for avatar_speaking event
      // This eliminates the 3-second delay between text appearing and speech starting
      if (finalText && data.emotion && !isSpeaking) {
        isSpeaking = true; // Set flag to prevent overlapping speech
        const speechStartTime = performance.now();
        
        // CRITICAL: Stop any ongoing speech first to prevent overlap
        azureCustomAvatarService.stopSpeaking()
          .then(() => {
            updateAudioState({ avatarSpeaking: true });
            
            return azureCustomAvatarService.speak(finalText, data.emotion || 'friendly');
          })
          .then(() => {
            const speechTime = performance.now() - speechStartTime;
            
            if (startTime) {
              const totalLatency = performance.now() - startTime;
              setMessageStartTime(null);
            }
            
            isSpeaking = false; // Clear flag
            updateAudioState({ avatarSpeaking: false });
          })
          .catch((error) => {
            isSpeaking = false; // Clear flag on error
            updateAudioState({ avatarSpeaking: false });
          });
      } else if (isSpeaking) {
        // Already speaking, skip
      }
    }
  };

  const handleAudioChunk = (data: any) => {
    const audioData = base64ToArrayBuffer(data.audio);
    audioService.playAudioData(audioData);
  };

  const handleSystemMessage = (data: any) => {
    const systemMessage = createSystemMessage(data.message);
    addMessage(systemMessage);
  };

  const handleAvatarInterrupted = async (data: any) => {
    try {
      await azureCustomAvatarService.stopSpeaking();
    } catch {}
  };

  const handleAvatarSpeak = (data: any) => {
    azureCustomAvatarService.speak(data.text).catch(() => {});
  };

  const handleUserMessageText = (data: any) => {
    debugLog("📨 [SessionEvents] Received user_message_text event:", data);
    debugLog("📨 [SessionEvents] Timestamp:", Date.now());
    debugLog("📨 [SessionEvents] manualSendInProgress:", manualSendInProgress);
    debugLog("📨 [SessionEvents] isInterim:", data.isInterim);

    // 🎯 Handle interim transcriptions for instant visual feedback
    // Interim = partial transcription as user speaks (real-time updates)
    // Final = complete transcription after user finishes (replaces interim)
    const isInterim = data.isInterim === true;

    if (isInterim) {
      // Interim transcriptions update the input box in real-time but don't add to chat
      // This gives instant visual feedback without creating duplicate messages
      debugLog("⚡ [SessionEvents] Interim transcription - updating input only");
      
      // Update the streaming transcription state to show in input box
      // This will be handled by the Chat component's streamingText logic
      // We don't add to messages yet - wait for final
      
      // Note: The input box is controlled by streamingTranscription state in Chat component
      // We rely on the existing STT infrastructure to handle interim display
      // This event is just for additional processing if needed
      return;
    }

    // Final transcription - add to chat and process normally
    debugLog("✅ [SessionEvents] Final transcription - adding to chat");
    
    // Always add user message from backend (backend is source of truth)
    const userMessage = createUserMessage(data.text);
    debugLog("💬 [SessionEvents] Adding user message to chat:", userMessage);
    debugLog("💬 [SessionEvents] Message text:", data.text);
    addMessage(userMessage);
    debugLog("✅ [SessionEvents] User message added to chat");

    // CRITICAL: Mute mic when answer is being processed
    // This prevents stray speech from being captured and attached to the next question
    // NOTE: We keep the recognizer WARM (running) to avoid reconnection latency
    debugLog("🔇 [SessionEvents] Muting microphone - answer being processed");
    speechToTextSileroService.setMuted(true);
    if (setMuted) setMuted(true);

    // Show thinking indicator while waiting for avatar response
    if (setIsThinking) {
      debugLog("💭 [SessionEvents] Setting isThinking to TRUE");
      setIsThinking(true);
    }

    // NOTE: Do NOT reset manualSendInProgress here!
    // The flag must stay TRUE to block late-arriving transcription events from Azure STT.
    // Azure may still be processing audio that was captured before the mic was muted.
    // The 5-second fallback timeout in useMessageHandling will reset the flag.
    if (manualSendInProgress) {
      debugLog("🔒 [SessionEvents] Keeping manualSendInProgress = TRUE (5s timeout will reset)");
    }
  };

  const handleAvatarReady = () => {
    setSessionState((prev: any) => ({ ...prev, avatarReady: true }));
  };

  const handleToolCallStart = (data: any) => {
    const toolCall: ToolCall = {
      id: data.id,
      name: data.name,
      status: "active",
      timestamp: new Date(),
    };
    addToolCall(toolCall);
  };

  const handleToolCallComplete = (data: any) => {
    updateToolCall(data.id, {
      status: "completed",
      result: data.result,
    });
  };

  const handleToolCallError = (data: any) => {
    updateToolCall(data.id, {
      status: "error",
      result: data.error,
    });
  };

  const handleConnectionError = (data: any) => {
    setSessionState((prev: any) => ({ ...prev, isConnecting: false }));
    toast({
      title: "Connection Error",
      description: data.message || "An unknown error occurred",
      variant: "error",
    });
  };

  const handleSessionError = (data: any) => {
    setSessionState((prev: any) => ({
      ...prev,
      isConnecting: false,
      isConnected: false,
      error: data.error,
    }));
    toast({
      title: "Session Error",
      description: data.error || "Failed to start session",
      variant: "error",
    });
  };

  const handleInterviewCompleted = async (data: any) => {
    debugLog("🏁 Interview completed event received:", data);
    
    // Trigger the completed dialog with completion reason
    window.dispatchEvent(new CustomEvent('interview_completed', {
      detail: {
        message: data.message,
        sessionId: data.sessionId,
        completionReason: data.completionReason || 'normal' // 'timeout' or 'normal'
      }
    }));

    // Disconnect avatar and services after interview completion
    debugLog("🔌 Disconnecting avatar and services after interview completion...");
    try {
      // Stop avatar first
      if (azureCustomAvatarService && typeof azureCustomAvatarService.stopAvatarSession === 'function') {
        debugLog("🎭 Stopping avatar session...");
        await azureCustomAvatarService.stopAvatarSession();
      }
      
      // Then disconnect connection manager (which stops audio and SignalR)
      await connectionManager.disconnect();
      debugLog("✅ Successfully disconnected avatar and services");
    } catch (error) {
      debugError("❌ Error disconnecting services:", error);
    }
  };

  connectionManager.on(SOCKET_EVENTS.SESSION_STARTED, handleSessionStarted);
  connectionManager.on(SOCKET_EVENTS.SESSION_ERROR, handleSessionError);
  connectionManager.on(SOCKET_EVENTS.INTERVIEW_COMPLETED, handleInterviewCompleted);
  connectionManager.on(SOCKET_EVENTS.AVATAR_SPEAKING, handleAvatarSpeaking);
  connectionManager.on(SOCKET_EVENTS.USER_MESSAGE_TEXT, handleUserMessageText);
  connectionManager.on(SOCKET_EVENTS.RESPONSE_TEXT, handleResponseText);
  connectionManager.on(SOCKET_EVENTS.AUDIO_CHUNK, handleAudioChunk);
  connectionManager.on(SOCKET_EVENTS.SYSTEM_MESSAGE, handleSystemMessage);
  connectionManager.on(
    SOCKET_EVENTS.AVATAR_INTERRUPTED,
    handleAvatarInterrupted
  );
  connectionManager.on(SOCKET_EVENTS.AVATAR_SPEAK, handleAvatarSpeak);
  connectionManager.on(SOCKET_EVENTS.AVATAR_READY, handleAvatarReady);
  connectionManager.on(SOCKET_EVENTS.TOOL_CALL_START, handleToolCallStart);
  connectionManager.on(
    SOCKET_EVENTS.TOOL_CALL_COMPLETE,
    handleToolCallComplete
  );
  connectionManager.on(SOCKET_EVENTS.TOOL_CALL_ERROR, handleToolCallError);
  connectionManager.on(SOCKET_EVENTS.CONNECTION_ERROR, handleConnectionError);

  return () => {
    window.removeEventListener('avatar_ready', handleAvatarReadyFromComponent);
    window.removeEventListener('avatar_request_config', handleAvatarRequestConfig);
    window.removeEventListener('avatar-speaking-start', handleAvatarSpeakingStart);
    connectionManager.off(SOCKET_EVENTS.SESSION_STARTED, handleSessionStarted);
    connectionManager.off(SOCKET_EVENTS.SESSION_ERROR, handleSessionError);
    connectionManager.off(SOCKET_EVENTS.INTERVIEW_COMPLETED, handleInterviewCompleted);
    connectionManager.off(SOCKET_EVENTS.AVATAR_SPEAKING, handleAvatarSpeaking);
    connectionManager.off(
      SOCKET_EVENTS.USER_MESSAGE_TEXT,
      handleUserMessageText
    );
    connectionManager.off(SOCKET_EVENTS.RESPONSE_TEXT, handleResponseText);
    connectionManager.off(SOCKET_EVENTS.AUDIO_CHUNK, handleAudioChunk);
    connectionManager.off(SOCKET_EVENTS.SYSTEM_MESSAGE, handleSystemMessage);
    connectionManager.off(
      SOCKET_EVENTS.AVATAR_INTERRUPTED,
      handleAvatarInterrupted
    );
    connectionManager.off(SOCKET_EVENTS.AVATAR_SPEAK, handleAvatarSpeak);
    connectionManager.off(SOCKET_EVENTS.AVATAR_READY, handleAvatarReady);
    connectionManager.off(SOCKET_EVENTS.TOOL_CALL_START, handleToolCallStart);
    connectionManager.off(
      SOCKET_EVENTS.TOOL_CALL_COMPLETE,
      handleToolCallComplete
    );
    connectionManager.off(SOCKET_EVENTS.TOOL_CALL_ERROR, handleToolCallError);
    connectionManager.off(
      SOCKET_EVENTS.CONNECTION_ERROR,
      handleConnectionError
    );
  };
}
